// Multi-territory Exchange Rates admin API service
import {
    MT_EXCHANGE_RATE_BASE_CURRENCY_API,
    MT_EXCHANGE_RATE_DOWNLOAD_API,
    MT_EXCHANGE_RATE_LIST_API,
    MT_EXCHANGE_RATE_UPDATE_API,
} from '@/multi-territory/mt-services/mt-api-endpoints'
import {
    MTApiResponse,
    mtBuildRequestHeader,
    mtHandleErrorResponse,
    mtHandleRequestFailure,
    mtParseJsonResponse,
    mtParseMessageResponse,
    mtUrlBuilder,
} from '@/multi-territory/mt-services/mt-api-utils'
import type {
    MTBaseCurrencyResponse,
    MTBaseCurrencyUpdateRequest,
    MTExchangeRateListRequest,
    MTExchangeRateListResponse,
    MTExchangeRateUpdateRequest,
} from '@/multi-territory/mt-types/mt-admin'

// Re-export types for consumer convenience
export type {
    MTBaseCurrencyResponse,
    MTBaseCurrencyUpdateRequest,
    MTExchangeRateItem,
    MTExchangeRateListRequest,
    MTExchangeRateListResponse,
    MTExchangeRateUpdateItem,
    MTExchangeRateUpdateRequest
} from '@/multi-territory/mt-types/mt-admin'

// --- API Functions ---

/**
 * Fetch exchange-rate listing (paginated, one row per territory) with the
 * current base currency and rate column label.
 * POST /wht-mt-admin/api/v1/exchange-rate/list
 */
export const getExchangeRateList = async (
  payload: MTExchangeRateListRequest
): Promise<MTApiResponse<MTExchangeRateListResponse>> => {
  const url = mtUrlBuilder(MT_EXCHANGE_RATE_LIST_API)
  const requestInit = mtBuildRequestHeader('POST', payload)
  try {
    const response = await fetch(url, requestInit)
    if (!response.ok) return await mtHandleRequestFailure(response)
    return await mtParseJsonResponse<MTExchangeRateListResponse>(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

/**
 * Persist edited exchange rates (bulk) for the current base currency.
 * PUT /wht-mt-admin/api/v1/exchange-rate/edit
 */
export const updateExchangeRates = async (
  payload: MTExchangeRateUpdateRequest
): Promise<MTApiResponse<string>> => {
  const url = mtUrlBuilder(MT_EXCHANGE_RATE_UPDATE_API)
  const requestInit = mtBuildRequestHeader('PUT', payload)
  try {
    const response = await fetch(url, requestInit)
    if (!response.ok) return await mtHandleRequestFailure(response)
    return await mtParseMessageResponse(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

/**
 * Get the current base currency and the list of selectable currencies.
 * GET /wht-mt-admin/api/v1/exchange-rate/base-currency
 */
export const getBaseCurrency = async (): Promise<
  MTApiResponse<MTBaseCurrencyResponse>
> => {
  const url = mtUrlBuilder(MT_EXCHANGE_RATE_BASE_CURRENCY_API)
  const requestInit = mtBuildRequestHeader('GET')
  try {
    const response = await fetch(url, requestInit)
    if (!response.ok) return await mtHandleRequestFailure(response)
    return await mtParseJsonResponse<MTBaseCurrencyResponse>(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

/**
 * Update the base currency used for reports & dashboards.
 * PUT /wht-mt-admin/api/v1/exchange-rate/base-currency
 */
export const updateBaseCurrency = async (
  payload: MTBaseCurrencyUpdateRequest
): Promise<MTApiResponse<string>> => {
  const url = mtUrlBuilder(MT_EXCHANGE_RATE_BASE_CURRENCY_API)
  const requestInit = mtBuildRequestHeader('PUT', payload)
  try {
    const response = await fetch(url, requestInit)
    if (!response.ok) return await mtHandleRequestFailure(response)
    return await mtParseMessageResponse(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

/**
 * Download exchange-rate data as Excel.
 * POST /wht-mt-admin/api/v1/exchange-rate/download
 */
export const downloadExchangeRates = async (
  searchCOList: unknown[] = []
): Promise<MTApiResponse<Blob>> => {
  const url = mtUrlBuilder(MT_EXCHANGE_RATE_DOWNLOAD_API)
  const requestInit = mtBuildRequestHeader('POST', { searchCOList })
  try {
    const response = await fetch(url, requestInit)
    if (!response.ok) return await mtHandleRequestFailure(response)
    const blob = await response.blob()
    return { success: true, data: blob }
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}
